import { apiRequest } from './client.js';

/**
 * System configuration (M01 §12 System Configuration, T006/T006c) — real
 * /v1/config contract. Values are text but typed via the catalogue value_type;
 * the UI renders a typed editor and the backend validates every write. SECRET
 * values are masked on read.
 */

export const PARAMETER_VALUE_TYPES = [
  'STRING',
  'INT',
  'DECIMAL',
  'BOOLEAN',
  'JSON',
  'DURATION',
  'BYTES',
] as const;
export type ParameterValueType = (typeof PARAMETER_VALUE_TYPES)[number];

/** A catalogue parameter + its effective value for the current tenant. */
export interface ConfigItem {
  readonly parameterCode: string;
  readonly valueType: ParameterValueType;
  readonly description: string;
  readonly defaultValueText: string | null;
  readonly allowedMin: number | null;
  readonly allowedMax: number | null;
  readonly allowedRegex: string | null;
  readonly allowedEnum: readonly unknown[] | null;
  readonly sensitivity: string;
  readonly changeRequiresRestart?: boolean;
  readonly value: string | null;
  readonly hasOverride: boolean;
  readonly isSecret: boolean;
}

/**
 * PUT /v1/config/:code response. AdminConfigController.upsert returns the
 * resolved value state ONLY — catalogue metadata (value type, default, allowed
 * range/regex/enum) is not echoed back, because a value write cannot change it.
 */
export interface ConfigValueUpdate {
  readonly parameterCode: string;
  readonly scope: string;
  readonly value: string | null;
  readonly hasOverride: boolean;
  readonly isSecret: boolean;
}

type Tok = string | null | undefined;

function unwrap(
  r: { items: readonly ConfigItem[] } | readonly ConfigItem[] | null,
): readonly ConfigItem[] {
  if (!r) return [];
  return Array.isArray(r)
    ? r
    : (r as { items: readonly ConfigItem[] }).items ?? [];
}

/** GET /v1/config — catalogue + effective values for the current tenant. */
export async function listConfig(token?: Tok): Promise<readonly ConfigItem[]> {
  const r = await apiRequest<
    { items: readonly ConfigItem[] } | readonly ConfigItem[]
  >('/config', { token });
  return unwrap(r);
}

/** GET /v1/config/:code — a single parameter with metadata + value. */
export function getConfig(code: string, token?: Tok): Promise<ConfigItem> {
  return apiRequest<ConfigItem>(`/config/${encodeURIComponent(code)}`, {
    token,
  });
}

/** PUT /v1/config/:code — validate + upsert a tenant override. */
export function upsertConfig(
  code: string,
  valueText: string,
  token?: Tok,
): Promise<ConfigValueUpdate> {
  return apiRequest<ConfigValueUpdate>(`/config/${encodeURIComponent(code)}`, {
    method: 'PUT',
    token,
    body: { valueText },
  });
}

/** DELETE /v1/config/:code — remove the tenant override (revert to default). */
export function deleteConfigOverride(
  code: string,
  token?: Tok,
): Promise<null> {
  return apiRequest<null>(`/config/${encodeURIComponent(code)}`, {
    method: 'DELETE',
    token,
  });
}
